import '@/i18n';
import * as Sentry from '@sentry/react';
import { Amplify } from 'aws-amplify';
import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { syncCookieConsentWithCognito } from './utils/cookieConsentSync';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { awsconfig } from './aws-exports.ts';

import ScrollToTop from '@/components/ScrollToTop/ScrollToTop';
import { AppContext } from './context/Context';
import { CustomRoutes } from './router/AppRouter';
import Footer from '@/components/Home/Footer';
import { AlertManager } from './modules/alerts/components/AlertManager';
import { AlertModalProvider } from './modules/alerts/context/AlertModalContext';
import { AcademicExpirationProvider } from './context/AcademicExpirationContext';
import { CmsTextsSync } from '@/components/CmsTextsSync';
import GlobalTrialBanner from '@/components/Layout/GlobalTrialBanner';
import ChatWidget from './components/ChatWidget/index';
import RenewalConfirmationModal from '@/components/Academic/RenewalConfirmationModal';
import { useRenewalConfirmation } from './hooks/useRenewalConfirmation';

// Migration utilities
import { MigrationProvider } from './store/migration/MigrationProvider';
import { AuthMigrationWrapper } from './store/migration/AuthMigrationWrapper';
import { getEmailLists } from './services/emailLists.ts';

// Extend Window interface
declare global {
  interface Window {
    syncCookieConsentWithCognito?: typeof syncCookieConsentWithCognito;
  }
}

// Configure Amplify in index file or root file
Amplify.configure(awsconfig as any);

if (typeof window !== 'undefined') {
  window.syncCookieConsentWithCognito = syncCookieConsentWithCognito;
}

// Configure Sentry
if (process.env.NODE_ENV !== 'production') {
  Sentry.init({
    dsn: 'https://aba19d681a08769aed34511cdaf4a06b@o4506586512228352.ingest.us.sentry.io/4506586513997824',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      // 'localhost',
      // /^https:\/\/disgenet\.com/,
      /^https:\/\/test\.medbioinformatics\.com/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

export const App = () => {
  const location = useLocation();
  const isSignupPage = location.pathname === '/free-trial-apply' || location.pathname === '/academic-apply';
  const { showConfirmationModal, confirmationMessage, closeConfirmationModal } = useRenewalConfirmation();
  const [listsReady, setListsReady] = useState(false);

  useEffect(() => {
    getEmailLists().then(() => setListsReady(true));
  }, []);

  return (
    <AuthMigrationWrapper>
      {({ contextValue, currentUser }) => (
        <React.Fragment>
          <AppContext.Provider value={contextValue}>
            <AcademicExpirationProvider
              user={contextValue.user}
              isAuthenticated={contextValue.isAuthenticated}
            >
              <div
                className="app-root-layout"
                style={{
                  minHeight: '100vh',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: '#f5f5f5',
                }}
              >
                <div
                  style={{
                    flex: isSignupPage ? '1 0 auto' : 1,
                    minHeight: isSignupPage ? 'auto' : 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CustomRoutes />
                  <GlobalTrialBanner />
                  <RenewalConfirmationModal
                    open={showConfirmationModal}
                    onClose={closeConfirmationModal}
                    message={confirmationMessage}
                  />
                  <ScrollToTop />
                  <div className="row">
                    <AlertManager userInfo={currentUser} variant="pink" />
                  </div>
                  <ChatWidget />
                </div>
                <Footer />
              </div>
            </AcademicExpirationProvider>
          </AppContext.Provider>
        </React.Fragment>
      )}
    </AuthMigrationWrapper>
  );
};

if (window.location.hash.startsWith('#!')) {
  const fromHash = window.location.hash.slice(2);
  const [pathOnly, queryInHash] = fromHash.split('?');
  const path = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
  const search = queryInHash ? `?${queryInHash}` : window.location.search;
  history.replaceState(history.state, '', `${path}${search}`);
}

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');
createRoot(root).render(
  <BrowserRouter
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    }}
  >
    <React.StrictMode>
      <HelmetProvider>
        <AlertModalProvider>
          <CmsTextsSync />
          <MigrationProvider
            initialFlags={{
              useAuthStore: true, // Start with auth migration
              useRestrictionsStore: false,
              useNavigationStore: false,
              useTableStore: false,
              useInfoSidebarStore: false,
            }}
          >
            <App />
          </MigrationProvider>
        </AlertModalProvider>
      </HelmetProvider>
    </React.StrictMode>
  </BrowserRouter>,
);
